
import React, { useEffect, useRef, useState } from 'react';
import { Shift, Position, Staff, getJSTDateString, timeToMinutes, minutesToTime, DailyEvent, api, getNotificationChannelLabel, Duty } from '../../api';
import { sMin as startMinOf, eMin as endMinOf, clockLabel, isoRange, rangeLabel } from '../../utils/shiftTime';
import { DutyMarks } from '../StaffDuties';
import { X, Siren, UserPlus, Plus } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import Dialog from '../ui/Dialog';
import Modal from '../ui/Modal';

interface ShiftDailyTimelineViewProps {
    currentDate: Date;
    confirmedShifts: Shift[];
    events: DailyEvent[];
    positions: Position[];
    allStaff: Staff[];
    baseStartTime?: string;
    baseEndTime?: string;
    onEventClick?: (dateStr: string, event?: DailyEvent) => void;
    onDataChange?: () => void;
    markDuties?: Duty[];
    // 表示中の店舗（シフトの追加に使う）
    storeId?: string;
}

const ShiftDailyTimelineView: React.FC<ShiftDailyTimelineViewProps> = ({
    currentDate, confirmedShifts, events, positions, allStaff,
    baseStartTime = '09:00',
    baseEndTime = '22:00',
    onEventClick,
    onDataChange,
    markDuties = [],
    storeId = ''
}) => {
    const { showToast } = useToast();
    const dateStr = getJSTDateString(currentDate);
    
    const [isLoading, setIsLoading] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [selectedSlotForAssign, setSelectedSlotForAssign] = useState<Shift | null>(null);
    const [dialog, setDialog] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void}>({
        isOpen: false, title: '', message: '', onConfirm: () => {}
    });

    // 表示日のシフトのみを対象にする（渡されるデータは月全体のことがある）。
    const activeTimelineShifts = confirmedShifts.filter(sh => sh.start.startsWith(dateStr));

    const dayEvents = events.filter(e => {
        const start = e.date;
        const end = e.endDate || e.date;
        return dateStr >= start && dateStr <= end;
    });

    // 表示する時間の範囲：店舗の基本時間に加えて、シフトが範囲外（早朝・深夜営業）なら広げる
    let startTotalMinutes = timeToMinutes(baseStartTime);
    let endTotalMinutes = timeToMinutes(baseEndTime);
    if (endTotalMinutes <= startTotalMinutes) endTotalMinutes += 24 * 60;
    if (activeTimelineShifts.length > 0) {
        startTotalMinutes = Math.min(startTotalMinutes, Math.floor(Math.min(...activeTimelineShifts.map(startMinOf)) / 60) * 60);
        endTotalMinutes = Math.max(endTotalMinutes, Math.ceil(Math.max(...activeTimelineShifts.map(endMinOf)) / 60) * 60);
    }
    
    const displayRangeMinutes = endTotalMinutes - startTotalMinutes;
    const totalSlots = Math.ceil(displayRangeMinutes / 30);
    const SLOTS = Array.from({length: totalSlots}, (_, i) => i);

    const getPosStyle = (start: string, end: string) => {
        const sMin = startMinOf({ start }); const eMin = endMinOf({ start, end });

        const left = ((sMin - startTotalMinutes) / displayRangeMinutes) * 100;
        const width = ((eMin - sMin) / displayRangeMinutes) * 100;
        
        return { 
            left: `${Math.max(-100, Math.min(200, left))}%`, 
            width: `${Math.max(0, width)}%`,
            display: (left + width < 0 || left > 100) ? 'none' : 'flex'
        };
    };

    const handleRemoveStaff = (shift: Shift) => {
        const staff = allStaff.find(s => s.id === shift.staffId);
        setDialog({
            isOpen: true,
            title: 'シフトの取り消し（欠勤反映）',
            message: `${staff?.lastName} ${staff?.firstName} さんの本日のシフトを外しますか？\n外した後、人数が足りなくなる時間帯は「不足」として表示され、そこから割り当てやSOSができます。`,
            onConfirm: async () => {
                setIsLoading(true);
                setDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    await api.deleteShift(shift.id, true);
                    showToast('シフトを外しました。足りない時間帯は不足として表示します。');
                    if (onDataChange) onDataChange();
                } catch (e) {
                    showToast('処理に失敗しました。', 'error');
                } finally { setIsLoading(false); }
            }
        });
    };

    const handleSendSos = async (shift: Shift) => {
        const channel = getNotificationChannelLabel(await api.getStore(shift.store));
        setDialog({
            isOpen: true,
            title: 'SOS（助っ人依頼）の送信',
            message: `この時間帯の不足について、出勤可能なスタッフへ${channel}でSOSを送信しますか？\n\n送信後、スタッフが引き受けると自動的にシフトが確定し、管理者にも${channel}で報告が届きます。`,
            onConfirm: async () => {
                setIsLoading(true);
                setDialog(prev => ({ ...prev, isOpen: false }));
                try {
                    await api.sendSosNotification({ 
                        storeId: shift.store, 
                        shiftId: shift.sourceVacancyId || shift.id, 
                        messageType: 'manual' 
                    });
                    showToast('SOSを送信しました。');
                    if (onDataChange) onDataChange();
                } catch (e) {
                    showToast('送信に失敗しました。', 'error');
                } finally { setIsLoading(false); }
            }
        });
    };

    const handleManualAssign = (vacancy: Shift) => {
        setSelectedSlotForAssign(vacancy);
        setIsAssignModalOpen(true);
    };

    const processAssign = async (staff: Staff) => {
        if (!selectedSlotForAssign) return;
        setIsLoading(true);
        try {
            const isVacancy = selectedSlotForAssign.type === 'vacancy';
            const { id: _slotId, headcount: _hc, openCall: _oc, applicants: _ap, offeredTo: _of, sourceVacancyId, ...slot } = selectedSlotForAssign;
            const newShift: Shift = {
                ...slot,
                id: `asg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                staffId: staff.id,
                type: isVacancy ? 'request' : selectedSlotForAssign.type,
                confirmed: true,
                label: '電話アサイン',
                ...(isVacancy && selectedSlotForAssign.label === '欠員' ? { vacancyId: sourceVacancyId || selectedSlotForAssign.id } : {})
            };

            await api.saveShifts([newShift]);

            if (isVacancy) {
                // 不足は時間帯ごとの人数で判定するので、枠は残したままでよい（埋まった時間は不足として表示されなくなる）。
                await api.sendSosResolvedNotification({
                    storeId: selectedSlotForAssign.store,
                    shiftId: sourceVacancyId || selectedSlotForAssign.id,
                    assignedStaffName: `${staff.lastName} ${staff.firstName}`
                });
            }
            
            setIsAssignModalOpen(false);
            showToast(`${staff.lastName} さんをアサインし、SOS募集を停止しました。`);
            if (onDataChange) onDataChange();
        } catch (e) {
            showToast('アサインに失敗しました。', 'error');
        } finally { setIsLoading(false); }
    };

    // ---- 確定済みシフトの調整（ドラッグで時間をずらす・端で開始／終了を変える・クリックで変更画面） ----
    const timelineRef = useRef<HTMLDivElement>(null);
    type DragState = { shift: Shift; mode: 'move' | 'start' | 'end'; startX: number; slotPx: number; delta: number };
    const [drag, setDrag] = useState<DragState | null>(null);
    const dragRef = useRef<DragState | null>(null);
    dragRef.current = drag;
    const [editing, setEditing] = useState<null | { shift: Shift; start: string; end: string; positionId: string; staffId: string }>(null);
    const [adding, setAdding] = useState<null | { positionId: string; staffId: string; start: string; end: string }>(null);
    const SNAP = 30;

    const shiftTimes = (shift: Shift, mode: DragState['mode'], delta: number) => {
        // 夜をまたぐシフトも扱えるよう、開始日の0時からの分で計算する（開始はその日のうち、終了は翌日まで）
        let s0 = startMinOf(shift); let e0 = endMinOf(shift);
        const d = delta * SNAP;
        if (mode === 'move') { s0 += d; e0 += d; }
        else if (mode === 'start') s0 = Math.min(s0 + d, e0 - SNAP);
        else e0 = Math.max(e0 + d, s0 + SNAP);
        // 開始はその日のうち（0:00〜23:30）。移動のときは終了も同じだけずらし、開始の変更では終了は動かさない
        if (s0 < 0) { if (mode === 'move') e0 -= s0; s0 = 0; }
        if (s0 > 24 * 60 - SNAP) { if (mode === 'move') e0 -= s0 - (24 * 60 - SNAP); s0 = Math.min(24 * 60 - SNAP, e0 - SNAP); }
        e0 = Math.min(s0 + 24 * 60 - SNAP, e0);
        return { start: minutesToTime(s0), end: minutesToTime(e0), s: s0, e: e0 };
    };
    const staffName = (id: string | null) => { const st = allStaff.find(x => x.id === id); return st ? `${st.lastName} ${st.firstName}` : ''; };
    const md = `${Number(dateStr.slice(5, 7))}/${Number(dateStr.slice(8, 10))}`;

    const saveTimes = async (shift: Shift, start: string, end: string, positionId = shift.positionId, staffId = shift.staffId) => {
        // 終了が開始以前なら翌日の終了として扱う（深夜営業。例：22:00〜02:00）
        if (start === end) { showToast('開始時刻と終了時刻が同じです。', 'error'); return false; }
        const { start: newStart, end: newEnd } = isoRange(dateStr, start, end);
        const label = rangeLabel({ start: newStart, end: newEnd });
        if (staffId && await api.checkStaffShiftOverlap(staffId, newStart, newEnd, shift.id)) { showToast('同じ時間帯に、このスタッフの別のシフトがあります。', 'error'); return false; }
        setIsLoading(true);
        try {
            // ポジションを変えた場合は、欠員枠の補充ではなくなる
            await api.saveShifts([{ ...shift, start: newStart, end: newEnd, positionId, staffId, ...(positionId !== shift.positionId ? { vacancyId: null } : {}) }]);
            if (staffId !== shift.staffId) {
                if (shift.staffId) await api.notifyShiftChange(shift.staffId, 'シフトの取り消しを通知しました。', `${md} ${rangeLabel(shift)}`);
                if (staffId) await api.notifyShiftChange(staffId, 'シフトの追加を通知しました。', `${md} ${label}`);
            } else if (staffId) {
                await api.notifyShiftChange(staffId, 'シフトの変更を通知しました。', `${md} ${rangeLabel(shift)} → ${label}`);
            }
            onDataChange?.();
            return true;
        } catch (e) { showToast('変更に失敗しました。', 'error'); return false; }
        finally { setIsLoading(false); }
    };

    const beginDrag = (e: React.PointerEvent, shift: Shift, mode: DragState['mode']) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        const width = timelineRef.current?.getBoundingClientRect().width || 1;
        setDrag({ shift, mode, startX: e.clientX, slotPx: width / (displayRangeMinutes / SNAP), delta: 0 });
    };
    useEffect(() => {
        if (!drag) return;
        const onMove = (e: PointerEvent) => {
            const cur = dragRef.current; if (!cur) return;
            const delta = Math.round((e.clientX - cur.startX) / cur.slotPx);
            if (delta !== cur.delta) setDrag({ ...cur, delta });
        };
        const onUp = async () => {
            const cur = dragRef.current; setDrag(null);
            if (!cur) return;
            if (cur.delta === 0) {
                if (cur.mode === 'move') setEditing({ shift: cur.shift, start: cur.shift.start.slice(11, 16), end: cur.shift.end.slice(11, 16), positionId: cur.shift.positionId || '', staffId: cur.shift.staffId || '' });
                return;
            }
            const t = shiftTimes(cur.shift, cur.mode, cur.delta);
            await saveTimes(cur.shift, t.start, t.end);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp, { once: true });
        return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
    }, [drag?.shift.id]);

    // 交代・追加の候補：この店舗のスタッフで、そのポジションを担当でき、その日にほかの勤務がない人
    const candidatesFor = (positionId: string, excludeShiftId?: string) => {
        const busy = new Set(activeTimelineShifts.filter(sh => sh.id !== excludeShiftId && sh.staffId && sh.type !== 'vacancy').map(sh => sh.staffId));
        return allStaff.filter(st => st.status !== 'resigned' && (!storeId || (st.stores || []).includes(storeId)) && (st.positionIds || []).includes(positionId) && !busy.has(st.id));
    };

    const timeLabels = [];
    for (let m = startTotalMinutes; m < endTotalMinutes; m += 60) { // 右端（終了時刻）のラベルははみ出すので出さない
        timeLabels.push({
            label: clockLabel(m), // 24時以降は「翌01:00」のように表示する
            percent: ((m - startTotalMinutes) / displayRangeMinutes) * 100
        });
    }

    // 同じ時間帯に重なるシフトを重ねずに表示するため、ポジションごとにレーン（段）を割り当てる。
    const LANE_H = 44; const LANE_GAP = 4; const ROW_PAD = 8;
    const minutesOf = (iso: string) => { const [d, t] = iso.split('T'); return timeToMinutes(t.slice(0, 5)) + Math.round((Date.parse(d) - Date.parse(dateStr)) / 86400000) * 24 * 60; };
    const laneMap = new Map<string, number>();
    const lanesByPos = new Map<string, number>();
    positions.forEach(pos => {
        const items = activeTimelineShifts.filter(sh => sh.positionId === pos.id).slice().sort((a, b) => minutesOf(a.start) - minutesOf(b.start) || minutesOf(a.end) - minutesOf(b.end));
        const laneEnds: number[] = [];
        items.forEach(sh => {
            const st = minutesOf(sh.start); const en = minutesOf(sh.end);
            let lane = laneEnds.findIndex(e => e <= st);
            if (lane === -1) { lane = laneEnds.length; laneEnds.push(en); } else { laneEnds[lane] = en; }
            laneMap.set(sh.id, lane);
        });
        lanesByPos.set(pos.id, Math.max(1, laneEnds.length));
    });
    const rowHeight = (posId: string) => ROW_PAD * 2 + (lanesByPos.get(posId) || 1) * LANE_H + ((lanesByPos.get(posId) || 1) - 1) * LANE_GAP;

    // ポジション色（bg-xxx-100）から、チップの塗り・枠・アクセントを導出する。
    const tone = (color: string) => {
        const base = (color.split(' ')[0] || 'bg-slate-100').replace(/^bg-/, '').replace(/-\d+$/, '');
        return { fill: `bg-${base}-50`, border: `border-${base}-200`, accent: `bg-${base}-500`, text: `text-${base}-700`, dot: `bg-${base}-400` };
    };
    const isToday = dateStr === getJSTDateString(new Date());
    const nowMinutes = (() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); })();
    const nowPercent = isToday && nowMinutes >= startTotalMinutes && nowMinutes <= endTotalMinutes ? ((nowMinutes - startTotalMinutes) / displayRangeMinutes) * 100 : null;

    return (
        <div className="flex flex-1 overflow-hidden relative select-none bg-white">
            <Dialog {...dialog} type="confirm" onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} />

            {/* 左：ポジション列 */}
            <div className="w-52 lg:w-60 flex-shrink-0 border-r border-slate-200 bg-slate-50 z-20">
                <div className="h-10 border-b border-slate-200 flex items-center px-4 text-[11px] font-bold text-slate-500">ポジション</div>
                {dayEvents.length > 0 && (
                    <div className="h-11 border-b border-slate-200 flex items-center px-4 bg-amber-50/60">
                        <span className="font-bold text-[11px] text-amber-700">イベント</span>
                    </div>
                )}
                {positions.map(pos => {
                    const t = tone(pos.color);
                    const count = activeTimelineShifts.filter(sh => sh.positionId === pos.id && sh.type !== 'vacancy').length;
                    // 同じ時刻に足りない人数の最大（重なる不足は合計する）
                    const pieces = activeTimelineShifts.filter(sh => sh.positionId === pos.id && sh.type === 'vacancy');
                    const shortage = Array.from(new Set(pieces.map(p => startMinOf(p)))).reduce((m, t) => Math.max(m, pieces.filter(p => startMinOf(p) <= t && endMinOf(p) > t).reduce((acc, p) => acc + (p.headcount || 1), 0)), 0);
                    return (
                        <div key={pos.id} className="border-b border-slate-100 flex items-center justify-between px-4 gap-2" style={{ height: rowHeight(pos.id) }}>
                            <div className="flex items-center min-w-0">
                                <span className={`w-2.5 h-2.5 rounded-full mr-2 shrink-0 ${t.dot}`}></span>
                                <span className="font-bold text-xs lg:text-sm text-slate-700 truncate">{pos.name}</span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <span className="text-[11px] font-bold text-slate-500 tabular-nums">{count}名</span>
                                <button type="button" onClick={() => setAdding({ positionId: pos.id, staffId: '', start: '10:00', end: '15:00' })} aria-label={`${pos.name}にシフトを追加`} title="シフトを追加" className="p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Plus size={14} /></button>
                                {shortage > 0 && <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5 tabular-nums">不足{shortage}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* 右：タイムライン */}
            <div className="flex-1 overflow-x-auto relative">
                <div ref={timelineRef} className="relative" style={{ minWidth: `${Math.max(640, totalSlots * 30)}px` }}>
                    <div className="h-10 border-b border-slate-200 bg-white sticky top-0 z-10 relative">
                        {timeLabels.map((tl, i) => (
                            <div key={i} className="absolute top-0 h-full flex items-center text-[11px] text-slate-500 font-medium tabular-nums px-1" style={{ left: `${tl.percent}%`, transform: i === 0 ? 'none' : (i === timeLabels.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)') }}>{tl.label}</div>
                        ))}
                    </div>

                    <div className="relative">
                        {/* グリッド線（1時間ごとに実線、30分は点線） */}
                        <div className="absolute inset-0 flex pointer-events-none">
                            {SLOTS.map((slot) => (
                                <div key={slot} className={`flex-1 border-r ${slot % 2 === 1 ? 'border-slate-200' : 'border-slate-100 border-dashed'}`}></div>
                            ))}
                        </div>
                        {/* 現在時刻 */}
                        {nowPercent !== null && (
                            <div className="absolute top-0 bottom-0 w-px bg-red-500 z-30 pointer-events-none" style={{ left: `${nowPercent}%` }}>
                                <span className="absolute -top-0 -translate-x-1/2 -translate-y-full text-[10px] font-bold text-white bg-red-500 rounded px-1.5 py-0.5 whitespace-nowrap">現在</span>
                            </div>
                        )}

                        {dayEvents.length > 0 && (
                            <div className="h-11 border-b border-slate-200 relative bg-amber-50/40 cursor-pointer" onClick={() => onEventClick?.(dateStr)}>
                                {dayEvents.map(ev => {
                                    const style = ev.isAllDay ? { left: '0.5%', width: '99%', display: 'flex' } : getPosStyle(`${dateStr}T${ev.startTime || '00:00'}:00`, `${dateStr}T${ev.endTime || '23:59'}:00`);
                                    return <div key={ev.id} onClick={(e) => { e.stopPropagation(); onEventClick?.(dateStr, ev); }} className="absolute top-2 bottom-2 border border-amber-300 rounded-md z-20 flex items-center px-3 bg-amber-100 text-amber-900" style={style as React.CSSProperties}><span className="text-[11px] font-bold truncate">{ev.title}</span></div>;
                                })}
                            </div>
                        )}

                        {positions.map(pos => {
                            const posShifts = activeTimelineShifts.filter(sh => sh.positionId === pos.id);
                            const t = tone(pos.color);
                            return (
                                <div key={pos.id} className="border-b border-slate-100 relative" style={{ height: rowHeight(pos.id) }}>
                                    {posShifts.map(shift => {
                                        const staff = allStaff.find(st => st.id === shift.staffId);
                                        const isVacancy = shift.type === 'vacancy';
                                        const dragging = !!drag && drag.shift.id === shift.id;
                                        const preview = dragging ? shiftTimes(shift, drag!.mode, drag!.delta) : null;
                                        const sm = preview ? preview.s : startMinOf(shift); const em = preview ? preview.e : endMinOf(shift);
                                        const startTime = clockLabel(sm); const endTime = clockLabel(em);
                                        const posIso = isoRange(dateStr, minutesToTime(sm), minutesToTime(em));
                                        return (
                                            <div
                                                key={shift.id}
                                                className={`absolute rounded-md flex items-stretch overflow-hidden border transition-colors group/item ${dragging ? 'z-40 ring-2 ring-blue-400' : 'z-20'} ${isVacancy ? 'bg-red-50 border-red-300 border-dashed' : `${t.fill} ${t.border} hover:ring-2 hover:ring-offset-0 hover:ring-slate-300 cursor-grab active:cursor-grabbing touch-none`}`}
                                                style={{ ...getPosStyle(posIso.start, posIso.end), top: ROW_PAD + (laneMap.get(shift.id) || 0) * (LANE_H + LANE_GAP), height: LANE_H }}
                                                onPointerDown={isVacancy ? undefined : (e) => beginDrag(e, shift, 'move')}
                                                data-testid={isVacancy ? undefined : `shift-${shift.id}`}
                                                title={isVacancy ? `不足 ${shift.headcount || 1}名 ${startTime}〜${endTime}` : `${staff ? `${staff.lastName} ${staff.firstName}` : '未定'} ${startTime}〜${endTime}`}
                                            >
                                                {!isVacancy && <span className={`w-1 shrink-0 ${t.accent}`}></span>}
                                                {!isVacancy && <span onPointerDown={(e) => beginDrag(e, shift, 'start')} className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize z-30" aria-label="開始時刻を変更" title="開始時刻を変更" />}
                                                {!isVacancy && <span onPointerDown={(e) => beginDrag(e, shift, 'end')} className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize z-30" aria-label="終了時刻を変更" title="終了時刻を変更" />}
                                                {isVacancy ? (
                                                    <div className="flex items-center justify-between w-full px-2 gap-2 min-w-0">
                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                            <Siren size={12} className="text-red-500 shrink-0" />
                                                            <span className="text-[11px] font-bold text-red-600 truncate">不足 {shift.headcount || 1}名</span>
                                                            <span className="text-[10px] text-red-400 tabular-nums whitespace-nowrap hidden sm:inline">{startTime}〜{endTime}</span>
                                                        </div>
                                                        <div className="flex gap-1 shrink-0">
                                                            <button onPointerDown={(e) => e.stopPropagation()} onClick={() => handleSendSos(shift)} className="px-2 py-0.5 bg-red-600 text-white rounded text-[10px] font-bold whitespace-nowrap hover:bg-red-700 transition-colors flex items-center gap-1"><Siren size={11} />SOS送信</button>
                                                            <button onClick={() => handleManualAssign(shift)} className="px-2 py-0.5 bg-slate-800 text-white rounded text-[10px] font-bold whitespace-nowrap hover:bg-black transition-colors flex items-center gap-1"><UserPlus size={11} />割り当て</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="relative flex-1 min-w-0 flex flex-col justify-center px-2">
                                                        <div className="text-[12px] font-bold text-slate-800 pr-5 leading-tight flex items-center gap-1 min-w-0">
                                                            <span className="truncate">{staff ? `${staff.lastName} ${staff.firstName}` : '未定'}</span>
                                                            <DutyMarks staff={staff} duties={markDuties} size="xs" />
                                                        </div>
                                                        <div className={`text-[10px] font-medium tabular-nums ${t.text} opacity-80 leading-tight mt-0.5`}>
                                                            {startTime}〜{endTime}
                                                        </div>
                                                        <button
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                            onClick={() => handleRemoveStaff(shift)}
                                                            className="absolute top-1/2 -translate-y-1/2 right-1.5 w-5 h-5 bg-white/90 text-slate-400 rounded-full flex items-center justify-center border border-slate-200 hover:bg-red-500 hover:text-white hover:border-red-600 transition-all opacity-0 group-hover/item:opacity-100 z-30"
                                                            title="このシフトを外す"
                                                        >
                                                            <X size={11} strokeWidth={3} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <Modal isOpen={!!editing} onClose={() => setEditing(null)} title="シフトの変更" maxWidth="max-w-md">
                {editing && (() => {
                    const cands = candidatesFor(editing.positionId, editing.shift.id);
                    return (
                        <div className="space-y-5 pt-2" data-testid="shift-edit">
                            <p className="text-sm font-bold text-slate-800">{md}　{staffName(editing.shift.staffId)}</p>
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1.5 block">時間</label>
                                <div className="flex items-center gap-2">
                                    <input type="time" step={1800} value={editing.start} onChange={e => setEditing({ ...editing, start: e.target.value })} aria-label="開始時刻" className="flex-1 border border-slate-300 rounded px-2 py-2 text-sm" />
                                    <span className="text-slate-400">〜</span>
                                    <input type="time" step={1800} value={editing.end} onChange={e => setEditing({ ...editing, end: e.target.value })} aria-label="終了時刻" className="flex-1 border border-slate-300 rounded px-2 py-2 text-sm" />
                                </div>
                                {editing.start && editing.end && editing.start !== editing.end && <p className="text-xs font-bold text-slate-600 mt-1.5 tabular-nums" data-testid="time-preview">{rangeLabel(isoRange(dateStr, editing.start, editing.end))}（{(endMinOf(isoRange(dateStr, editing.start, editing.end)) - startMinOf({ start: isoRange(dateStr, editing.start, editing.end).start })) / 60}時間）</p>}
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1.5 block">ポジション</label>
                                <select value={editing.positionId} onChange={e => setEditing({ ...editing, positionId: e.target.value })} aria-label="ポジション" className="w-full border border-slate-300 rounded px-2 py-2 text-sm font-bold bg-white">
                                    {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1.5 block">担当（交代する場合は変更）</label>
                                <select value={editing.staffId} onChange={e => setEditing({ ...editing, staffId: e.target.value })} aria-label="担当" className="w-full border border-slate-300 rounded px-2 py-2 text-sm font-bold bg-white">
                                    <option value={editing.shift.staffId || ''}>{staffName(editing.shift.staffId)}</option>
                                    {cands.filter(c => c.id !== editing.shift.staffId).map(c => <option key={c.id} value={c.id}>{c.lastName} {c.firstName}</option>)}
                                </select>
                            </div>
                            <div className="pt-4 flex items-center justify-between gap-2 border-t">
                                <button type="button" onClick={() => { const sh = editing.shift; setEditing(null); handleRemoveStaff(sh); }} className="px-3 py-2 text-red-500 font-bold text-sm hover:bg-red-50 rounded">シフトを外す</button>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-slate-500 font-bold text-sm hover:bg-slate-100 rounded">キャンセル</button>
                                    <button type="button" disabled={isLoading} onClick={async () => { if (await saveTimes(editing.shift, editing.start, editing.end, editing.positionId, editing.staffId || null)) setEditing(null); }} className="px-6 py-2 bg-blue-600 text-white font-bold text-sm rounded hover:bg-blue-700">保存して通知</button>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            <Modal isOpen={!!adding} onClose={() => setAdding(null)} title="シフトの追加" maxWidth="max-w-md">
                {adding && (() => {
                    const cands = candidatesFor(adding.positionId);
                    return (
                        <div className="space-y-5 pt-2" data-testid="shift-add">
                            <p className="text-sm font-bold text-slate-800">{md}　{positions.find(p => p.id === adding.positionId)?.name}</p>
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1.5 block">スタッフ</label>
                                <select value={adding.staffId} onChange={e => setAdding({ ...adding, staffId: e.target.value })} aria-label="スタッフ" className="w-full border border-slate-300 rounded px-2 py-2 text-sm font-bold bg-white">
                                    <option value="">選択してください</option>
                                    {cands.map(c => <option key={c.id} value={c.id}>{c.lastName} {c.firstName}</option>)}
                                </select>
                                {cands.length === 0 && <p className="text-xs text-slate-500 mt-1">この日に追加できるスタッフがいません。</p>}
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1.5 block">時間</label>
                                <div className="flex items-center gap-2">
                                    <input type="time" step={1800} value={adding.start} onChange={e => setAdding({ ...adding, start: e.target.value })} aria-label="開始時刻" className="flex-1 border border-slate-300 rounded px-2 py-2 text-sm" />
                                    <span className="text-slate-400">〜</span>
                                    <input type="time" step={1800} value={adding.end} onChange={e => setAdding({ ...adding, end: e.target.value })} aria-label="終了時刻" className="flex-1 border border-slate-300 rounded px-2 py-2 text-sm" />
                                </div>
                                {adding.start && adding.end && adding.start !== adding.end && <p className="text-xs font-bold text-slate-600 mt-1.5 tabular-nums" data-testid="time-preview">{rangeLabel(isoRange(dateStr, adding.start, adding.end))}（{(endMinOf(isoRange(dateStr, adding.start, adding.end)) - startMinOf({ start: isoRange(dateStr, adding.start, adding.end).start })) / 60}時間）</p>}
                            </div>
                            <div className="pt-4 flex justify-end gap-2 border-t">
                                <button type="button" onClick={() => setAdding(null)} className="px-4 py-2 text-slate-500 font-bold text-sm hover:bg-slate-100 rounded">キャンセル</button>
                                <button type="button" disabled={!adding.staffId || isLoading} onClick={async () => {
                                    const draft: Shift = { id: `asg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, staffId: adding.staffId, store: storeId, start: `${dateStr}T${adding.start}:00`, end: `${dateStr}T${adding.end}:00`, type: 'request', positionId: adding.positionId, confirmed: true, label: '追加' };
                                    if (await saveTimes({ ...draft, staffId: null }, adding.start, adding.end, adding.positionId, adding.staffId)) setAdding(null);
                                }} className="px-6 py-2 bg-blue-600 text-white font-bold text-sm rounded hover:bg-blue-700 disabled:bg-slate-300">追加して通知</button>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            <Modal isOpen={isAssignModalOpen} onClose={() => setIsAssignModalOpen(false)} title="割り当て（電話で了承を得た場合など）" maxWidth="max-w-md">
                <div className="space-y-4 pt-2">
                    <p className="text-xs text-slate-500 font-bold mb-4">電話などで出勤が決まったスタッフを、この枠に直接アサインします。</p>
                    <div className="max-h-60 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                        {allStaff
                            .filter(s => s.status === 'active' && s.positionIds?.includes(selectedSlotForAssign?.positionId || ''))
                            .map(staff => (
                            <button 
                                key={staff.id} 
                                onClick={() => processAssign(staff)}
                                className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left shadow-sm active:scale-98"
                            >
                                <div className="flex items-center">
                                    
                                    <div>
                                        <div className="text-sm font-bold text-slate-800">{staff.lastName} {staff.firstName}</div>
                                        <div className="text-[10px] text-slate-400 font-bold">レベル：{staff.skills?.find(sk => sk.positionId === selectedSlotForAssign?.positionId)?.level || 0}</div>
                                    </div>
                                </div>
                                <UserPlus size={16} className="text-blue-500" />
                            </button>
                        ))}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ShiftDailyTimelineView;
